// English source strings for privacy (module 16) and integrations (module 18), ADR-020.
export const enPrivacy = {
  // ------------------------------------------------------------ API messages
  'api.privacyRetentionUnconfigured': 'Every record class needs an approved retention period before the rules can be published.',
  'api.privacyNoPublishedRetention': 'Publish retention rules before planning a purge.',
  'api.privacyFourEyes': 'Someone other than the person who planned this purge must apply it.',
  'api.privacyDryRun': 'A dry run is a preview only. Plan a new run to apply it.',
  'api.privacyErasureViaRequest': 'Apply an erasure from its subject request.',
  'api.privacyDraftExists': 'A draft already exists for this purpose. Approve or replace it first.',
  'api.privacyRequestState': 'This step is not available while the request is {status}.',
  'api.privacyNotVerified': "Verify the requester's identity first.",
  'api.privacyContactOnly': 'A shared contact detail alone cannot link this record. The name or email must match too.',
  'api.privacyNoMatch': "This record does not match the requester's details.",
  'api.privacyNoSubjects': 'Link at least one matching record first.',
  'api.privacyNoExport': 'Prepare the export first.',
  'api.privacyExportUnavailable': 'This export is not released or has expired.',
  'api.privacyNoPlan': 'Plan the erasure first.',
  'api.apiClientNameTaken': 'An active API client already has this name.',
  'api.apiRateLimited': 'Too many requests. Try again shortly.',
  'api.connectorNotEnabled': 'Enable the connector first.',
  'api.connectorTestRequired': 'Run a successful connection test of this configuration before enabling it.',
  'api.connectorUnknownAdapter': 'This adapter is not available in this build.',
  'api.operationNotUnknown': 'Only operations with an unknown outcome are reconciled.',
  'api.webhookDestination': 'This webhook address is not allowed.',
  'api.webhookDisabled': 'This subscription is disabled.',
  'v.reasonShort': 'Give a reason of at least 3 characters.',
  'v.acknowledgeRequired': 'Confirm that you understand this cannot be undone.',
  'v.apiScopeDuplicate': 'Each scope may be listed only once.',
  'v.apiScopeForbidden': 'You cannot give this access.',
  'v.apiScopeHumanOnly': 'This action is for people only and cannot be given to an API client.',
  'v.apiScopeInvalid': 'Choose a valid action and scope.',
  'v.privacyContactRequired': 'Record an email address or phone number for replies.',
  'v.privacyExtensionMax': 'At most {max} more days can be added.',
  'v.privacyHoldOwner': 'Choose an active person as the hold owner.',
  'v.privacyNoticeRequired': 'A consent purpose needs the notice that records the consent.',
  'v.secretRefInvalid': 'Use a secret reference such as env:NAME or vault:path#key.',
  'v.secretRefRequired': 'Secrets are never stored here. Use a secret reference instead.',
  'v.webhookEventForbidden': 'You cannot read the records this event describes.',
  'v.webhookEventUnknown': 'Unknown event type.',
  'v.webhookUrl.invalid_url': 'Enter a full address such as https://example.com/hooks.',
  'v.webhookUrl.scheme': 'Use https (plain http only for local receivers outside production).',
  'v.webhookUrl.credentials_in_url': 'Do not put credentials in the address.',
  'v.webhookUrl.blocked_address': 'Internal, link-local and metadata addresses are not allowed.',
  'v.webhookUrl.port': 'Use a valid port.',

  'specialist.privacy-administrator': 'Privacy administrator (retention, purposes, legal basis)',
  // ------------------------------------------------------------ request statuses (used in messages)
  'privacy.request.status.received': 'received',
  'privacy.request.status.verified': 'verified',
  'privacy.request.status.export_ready': 'export ready for review',
  'privacy.request.status.released': 'export released',
  'privacy.request.status.erasure_planned': 'erasure planned',
  'privacy.request.status.erasing': 'erasing',
  'privacy.request.status.completed': 'completed',
  'privacy.request.status.partial': 'partially completed',
  'privacy.request.status.rejected': 'rejected',
  'privacy.request.status.withdrawn': 'withdrawn',

  // ------------------------------------------------------------ UI (privacy and integrations pages)
  'pv.title': "Privacy & data lifecycle",
  'pv.intro': "Review identity, holds and per-store outcomes before completing a request.",
  'pv.legalTitle': "Legal values are owner decisions",
  'pv.legalBody': "Retention periods, legal bases and response deadlines here are configuration. Engineering defaults are placeholders, not legal advice, and nothing on this page claims compliance with any law.",
  'pv.tabsLabel': "Privacy sections",
  'pv.tab.requests': "Subject requests",
  'pv.tab.retention': "Retention & purposes",
  'pv.tab.holds': "Holds & backup handling",
  'pv.tab.purges': "Purge jobs",
  'pv.tab.history': "Action history",
  'pv.col.action': "Action",
  'pv.masked': "details masked",
  'pv.overdue': "Overdue",
  'pv.hold.active': "Active hold",
  'pv.hold.none': "No hold",
  'pv.req.title': "Data-subject request register",
  'pv.req.intro': "Requests stay incomplete while a mandatory store is unresolved.",
  'pv.req.new': "New subject request",
  'pv.req.newHint': "Record who asked and how. Identity is verified as a separate step.",
  'pv.req.empty': "No subject requests in your scope.",
  'pv.req.col.reference': "Reference / requester",
  'pv.req.col.type': "Request",
  'pv.req.col.status': "Status",
  'pv.req.col.due': "Due",
  'pv.req.col.hold': "Hold",
  'pv.req.review': "Review request",
  'pv.req.unverified': "Identity not verified",
  'pv.req.type': "Request type",
  'pv.req.site': "Site",
  'pv.req.siteHint': "Organisation-wide requests need an organisation-wide privacy grant to review.",
  'pv.req.requesterName': "Requester name",
  'pv.req.requesterEmail': "Reply email",
  'pv.req.requesterPhone': "Reply phone",
  'pv.req.receivedVia': "Received via",
  'pv.req.create': "Record request",
  'pv.req.requester': "Requester",
  'pv.req.contact': "Contact",
  'pv.req.received': "Received",
  'pv.req.identity': "Identity",
  'pv.req.holdNote': "An active hold covers this subject. Held records stay until an authorised person releases the hold.",
  'pv.req.method': "Verification method",
  'pv.req.whatChecked': "What was checked",
  'pv.req.whatCheckedHint': "Describe the check. Never record document numbers or copies.",
  'pv.req.linkHint': "Link only records that match the verified requester. A shared phone number alone is refused.",
  'pv.req.linked': { one: "{count} record linked", other: "{count} records linked" },
  'pv.req.isLinked': "Linked",
  'pv.req.link': "Link record",
  'pv.req.linkFirst': "Link the subject record first.",
  'pv.req.exports': "Exports",
  'pv.req.download': "Download",
  'pv.req.expires': "available until {at}",
  'pv.step.verify': "Record identity verification",
  'pv.step.link': "Link matching records",
  'pv.step.prepareExport': "Prepare export for review",
  'pv.step.releaseExport': "Release reviewed export",
  'pv.step.planErasure': "Plan erasure",
  'pv.step.applyErasure': "Apply erasure",
  'pv.step.correct': "Apply corrections",
  'pv.step.complete': "Complete request",
  'pv.step.extend': "Extend deadline by 14 days",
  'pv.step.withdraw': "Record withdrawal",
  'pv.step.reject': "Reject request",
  'pv.ackIrreversible': "I understand this deletes or anonymises data and cannot be undone.",
  'pv.applyHint': "Holds are checked again for every item when it runs. Another person must apply what you planned.",
  'pv.run.title': "Per-store plan",
  'pv.item.class': "Data class",
  'pv.item.store': "Store",
  'pv.item.method': "Action",
  'pv.item.status': "Outcome",
  'pv.ret.title': "Retention by data class",
  'pv.ret.intro': "Every class needs an approved period before rules can be published.",
  'pv.ret.loadDefaults': "Fill gaps with engineering defaults",
  'pv.ret.publish': "Publish draft",
  'pv.ret.discard': "Discard draft",
  'pv.ret.problems': { one: "{count} class still needs a period.", other: "{count} classes still need a period." },
  'pv.ret.class': "Data class",
  'pv.ret.trigger': "Clock starts",
  'pv.ret.published': "Published (v{number})",
  'pv.ret.draft': "Draft",
  'pv.ret.unconfigured': "Not configured",
  'pv.ret.days': { one: "{count} day", other: "{count} days" },
  'pv.ret.engineeringDefault': "Engineering default",
  'pv.ret.noStoreYet': "no data store yet",
  'pv.ret.daysFor': "Days for {name}",
  'pv.ret.saveDraft': "Save draft",
  'pv.ret.noInvent': "No period is invented: an empty class blocks publication.",
  'pv.pur.title': "Purposes and legal basis",
  'pv.pur.intro': "Each processing purpose records its approved basis; consent purposes link the notice whose acceptance is the consent record.",
  'pv.pur.empty': "No purposes recorded yet.",
  'pv.pur.purpose': "Purpose",
  'pv.pur.basis': "Legal basis",
  'pv.pur.consent': "Consent receipts",
  'pv.pur.consentLine': "v{version} {locale}: {accepted} accepted, {declined} declined",
  'pv.pur.withdrawals': { one: "{count} withdrawal", other: "{count} withdrawals" },
  'pv.pur.approve': "Approve",
  'pv.pur.code': "Purpose code",
  'pv.pur.nameEn': "Name (English)",
  'pv.pur.nameAr': "Name (Arabic)",
  'pv.pur.classes': "Data classes",
  'pv.pur.classesHint': "Comma-separated class codes, for example visit_metadata, documents.",
  'pv.pur.notice': "Consent notice key",
  'pv.pur.draft': "Draft purpose",
  'pv.set.title': "Request deadlines and safeguards",
  'pv.set.defaults': "Engineering defaults in use; the legal deadlines are an owner decision.",
  'pv.set.organisation': "Values set by your organisation.",
  'pv.set.requestResponseDays': "Days to answer a request",
  'pv.set.requestExtensionDays': "Maximum extension (days)",
  'pv.set.exportDownloadDays': "Export download window (days)",
  'pv.set.anonymityThreshold': "Smallest retained group",
  'pv.set.purgeFourEyes': "A second person must apply every purge",
  'pv.set.noAutoApply': "Scheduled planning only prepares runs; a person always applies them.",
  'pv.sen.title': "Sensitive-field grants",
  'pv.sen.intro': "Reading, revealing and exporting each class need their own grants; general visit access confers none of them.",
  'pv.sen.field': "Field class",
  'pv.sen.read': "Read",
  'pv.sen.reveal': "Reveal",
  'pv.sen.export': "Export",
  'pv.sen.yours': "You: {value}",
  'pv.hold.title': "Legal and investigation holds",
  'pv.hold.intro': "A hold stops scheduled deletion of exactly the records in its scope. Release is a separate authority.",
  'pv.hold.new': "Place hold",
  'pv.hold.newHint': "Keep the scope narrow; owner, reason and a review date are required.",
  'pv.hold.empty': "No holds.",
  'pv.hold.scope': "Scope",
  'pv.hold.reason': "Reason",
  'pv.hold.owner': "Accountable owner",
  'pv.hold.ownerHint': "User reference of the person accountable for this hold.",
  'pv.hold.review': "Review by",
  'pv.hold.target': "Record reference",
  'pv.hold.targetHint': "The person or visit reference (identifier).",
  'pv.hold.place': "Place hold",
  'pv.hold.release': "Release hold",
  'pv.hold.releaseWarning': "Once released, retention and approved erasure plans can delete these records.",
  'pv.bak.title': "Backup and restore handling",
  'pv.bak.intro': "Erased records leave identifiers-only tombstones. After any restore, tombstones are re-applied before the service reopens.",
  'pv.bak.empty': "No tombstones yet.",
  'pv.bak.count': "Tombstones",
  'pv.bak.reapplied': "Last re-applied",
  'pv.bak.never': "Never",
  'pv.bak.foot': "Backup expiry follows the deployment backup policy (owner decision); restored copies are re-erased by the tombstone run.",
  'pv.pg.title': "Retention purge jobs",
  'pv.pg.intro': "Plan or dry-run to preview what is due; a person applies, and the ledger shows every store outcome.",
  'pv.pg.kind': "Run type",
  'pv.pg.dryRun': "Dry run",
  'pv.pg.plan': "Plan",
  'pv.pg.empty': "No purge runs yet.",
  'pv.pg.run': "Run",
  'pv.pg.items': "Items",
  'pv.pg.open': "Open",
  'pv.pg.scheduled': "scheduled",
  'pv.pg.engineeringDefaults': { one: "{count} class uses an engineering default", other: "{count} classes use engineering defaults" },
  'pv.pg.aggregates': "Retained aggregates: {count} groups; {risky} small groups lose identifying detail.",
  'pv.pg.apply': "Apply purge",
  'pv.pg.retry': "Retry failed items",
  'pv.pg.cancel': "Cancel plan",
  'pv.his.title': "Privacy action history",
  'pv.his.intro': "Exports, erasures, holds, retention changes and reveals, with safe identifiers only.",
  'pv.his.empty': "No privacy actions recorded.",
  'pv.his.when': "When",
  'pv.his.action': "Action",
  'pv.his.record': "Record",
  'pv.his.outcome': "Outcome",
  'pv.field.givenName': "Given name",
  'pv.field.familyName': "Family name",
  'pv.field.email': "Email",
  'pv.field.phone': "Phone",
  'pv.type.export': "Access / export",
  'pv.type.correction': "Correction",
  'pv.type.erasure': "Erasure",
  'pv.via.email': "Email",
  'pv.via.web': "Web form",
  'pv.via.in_person': "In person",
  'pv.via.post': "Post",
  'pv.via.phone': "Phone",
  'pv.via.other': "Other",
  'pv.method.document_sighted': "Document sighted",
  'pv.method.authenticated_channel': "Authenticated channel",
  'pv.method.known_contact_challenge': "Challenge to known contact",
  'pv.method.authorised_representative': "Authorised representative",
  'pv.method.delete': "Delete",
  'pv.method.anonymise': "Anonymise",
  'pv.method.redact': "Redact",
  'pv.basis.email': "email",
  'pv.basis.phone': "phone",
  'pv.basis.name': "name",
  'pv.basis.legal.consent': "Consent",
  'pv.basis.legal.contract': "Contract",
  'pv.basis.legal.legal_obligation': "Legal obligation",
  'pv.basis.legal.vital_interests': "Vital interests",
  'pv.basis.legal.public_interest': "Public interest",
  'pv.basis.legal.legitimate_interests': "Legitimate interests",
  'pv.refusal.contact_only': "Contact detail only — refused",
  'pv.refusal.no_match': "No match",
  'pv.export.prepared': "Prepared — awaiting review",
  'pv.export.released': "Released",
  'pv.export.superseded': "Superseded",
  'pv.export.purged': "Deleted",
  'pv.count.people': "People",
  'pv.count.visits': "Visits",
  'pv.count.presenceEvents': "Presence events",
  'pv.count.acknowledgements': "Acknowledgements",
  'pv.count.documents': "Documents",
  'pv.count.messages': "Messages",
  'pv.count.photos': "Photos",
  'pv.count.history': "History entries",
  'pv.count.profileFields': "Profile fields",
  'pv.count.affiliations': "Organisation affiliations",
  'pv.count.profileLinks': "Profile link decisions",
  'pv.count.organisationContacts': "Organisation contact entries",
  'pv.count.rosterRows': "Staged roster rows",
  'pv.count.profileFieldsWithheld': "Profile fields withheld for review",
  'pv.exception.currently_onsite': "Currently on site",
  'pv.exception.active_visit': "Visit not yet ended",
  'pv.exception.shared_record': "Shared with other people",
  'pv.item.pending': "Pending",
  'pv.item.done': "Done",
  'pv.item.held': "Held",
  'pv.item.excepted': "Exception",
  'pv.item.failed': "Failed",
  'pv.run.status.planned': "Planned",
  'pv.run.status.applying': "Applying",
  'pv.run.status.completed': "Completed",
  'pv.run.status.partial': "Partially completed",
  'pv.run.status.cancelled': "Cancelled",
  'pv.pg.kind.retention': "Retention purge",
  'pv.pg.kind.anonymisation': "Anonymisation",
  'pv.pg.kind.erasure': "Subject erasure",
  'pv.pur.status.draft': "Draft",
  'pv.pur.status.approved': "Approved",
  'pv.pur.status.retired': "Retired",
  'pv.hold.kind.person': "Person",
  'pv.hold.kind.visit': "Visit",
  'pv.hold.kind.site': "Site",
  'pv.hold.kind.record_class': "Data class",
  'pv.hold.status.active': "Active",
  'pv.hold.status.released': "Released",
  'pv.store.database': "Database",
  'pv.store.object_store': "File store",
  'pv.trigger.visit_end': "Visit end",
  'pv.trigger.record_created': "Record created",
  'pv.trigger.request_closed': "Request closed",
  'pv.trigger.last_activity': "Last activity",
  'pv.his.actor.user': "Person",
  'pv.his.actor.automation': "API client",
  'pv.his.actor.system': "System",
  'pv.his.actor.anonymous': "Unauthenticated",
  'pv.his.actor.device': "Device",
  'pv.sen.class.identity_document': "Identity documents",
  'pv.sen.class.identity_number': "Identity numbers",
  'pv.sen.class.photo': "Photographs",
  'pv.sen.class.signature': "Signatures",
  'pv.sen.class.private_note': "Private notes",
  'pv.sen.class.contact': "Contact details",
  'pv.class.visit_metadata': "Visit metadata",
  'pv.class.identity_fields': "Identity fields",
  'pv.class.id_images': "ID images",
  'pv.class.identity_evidence': "Identity evidence values",
  'pv.class.photos': "Photos",
  'pv.class.signatures': "Signatures",
  'pv.class.documents': "Documents",
  'pv.class.access_events': "Access events",
  'pv.class.private_notes': "Private notes",
  'pv.class.messages': "Messages",
  'pv.class.plate_reads': "Plate reads",
  'pv.class.vehicle_photos': "Vehicle photos",
  'pv.class.vehicle_movements': "Vehicle movements",
  'pv.class.subject_requests': "Subject requests",
  'pv.class.privacy_exports': "Privacy exports",
  'pv.class.webhook_deliveries': "Webhook deliveries",
  'ig.title': "Integrations & devices",
  'ig.intro': "Connectivity and confirmed business outcomes are different signals.",
  'ig.simulatorNote': "Only simulators and local receivers exist in this build; no third-party system is contacted, and nothing here is certified support for a provider.",
  'ig.tabsLabel': "Integration sections",
  'ig.col.action': "Action",
  'ig.secretOnce': "Shown once",
  'ig.secretOnceBody': "Copy this value into the receiving system now. Passage stores only a digest or a sealed copy and will never show it again.",
  'ig.secretStored': "I have stored it",
  'ig.con.title': "Connectors",
  'ig.con.intro': "Transport health and business outcomes are tracked separately; unknown results need a person to reconcile.",
  'ig.con.add': "Add connector",
  'ig.con.addHint': "Secrets are references to the deployment secret store, never values.",
  'ig.con.empty': "No connectors configured.",
  'ig.con.connector': "Connector",
  'ig.con.connectivity': "Connectivity",
  'ig.con.business': "Business outcome",
  'ig.con.open': "Open",
  'ig.con.name': "Name",
  'ig.con.secretRef': "Secret reference: {name}",
  'ig.con.secretRefHint': "For example env:HR_API_TOKEN.",
  'ig.con.create': "Create connector",
  'ig.con.secrets': "Secret references",
  'ig.con.lastTest': "Last connection test",
  'ig.con.test': "Test connection",
  'ig.con.enable': "Enable",
  'ig.con.disable': "Disable",
  'ig.con.operation': "Operation",
  'ig.con.simulate': "Simulated outcome",
  'ig.con.run': "Run operation",
  'ig.con.history': "Operation history",
  'ig.con.readBack': "What the read-back showed",
  'ig.con.confirmed': "Confirmed",
  'ig.con.failed': "Did not happen",
  'ig.ad.title': "Adapter catalogue",
  'ig.ad.intro': "Each adapter declares what it supports; anything else returns \"unsupported\", never success.",
  'ig.ad.adapter': "Adapter",
  'ig.ad.operations': "Supported operations",
  'ig.ad.validation': "Validation",
  'ig.ad.none': "None yet",
  'ig.business.unknownCount': { one: "{count} unknown result", other: "{count} unknown results" },
  'ig.dev.title': "Device status",
  'ig.dev.intro': "Kiosks, printers, scanners and readers. Connectivity is not proof that a badge or scan succeeded.",
  'ig.dev.kind': "Device type",
  'ig.dev.name': "Device name",
  'ig.dev.add': "Register device",
  'ig.dev.empty': "No devices registered.",
  'ig.dev.device': "Device",
  'ig.dev.lastJob': "Last job result",
  'ig.dev.revoke': "Revoke",
  'ig.dev.revoked': "Revoked",
  'ig.dev.revokeReason': "Revoked from the device status view",
  'ig.dev.foot': "A device is shown disconnected when its heartbeat is older than its stale threshold.",
  'ig.dev.pairingCode': "Pairing code for {name}",
  'ig.wh.title': "Webhook subscriptions",
  'ig.wh.intro': "Signed events from committed changes only. Receivers de-duplicate on the event id.",
  'ig.wh.empty': "No webhook subscriptions.",
  'ig.wh.subscription': "Subscription",
  'ig.wh.events': "Event types",
  'ig.wh.deliveries': "Deliveries",
  'ig.wh.secretHint': "secret …{hint}",
  'ig.wh.counts': "{pending} pending · {delivered} delivered · {dead} dead",
  'ig.wh.lastVerification': "Verification: {outcome} · {at}",
  'ig.wh.log': "Delivery log",
  'ig.wh.verify': "Verify endpoint",
  'ig.wh.rotate': "Rotate secret",
  'ig.wh.pause': "Pause",
  'ig.wh.resume': "Resume",
  'ig.wh.disable': "Disable",
  'ig.wh.new': "New subscription",
  'ig.wh.newHint': "The endpoint must answer a signed challenge before deliveries start.",
  'ig.wh.name': "Name",
  'ig.wh.url': "Endpoint address",
  'ig.wh.urlHint': "https only; internal and metadata addresses are refused.",
  'ig.wh.create': "Create subscription",
  'ig.wh.signingSecret': "Webhook signing secret",
  'ig.wh.noDeliveries': "No deliveries yet.",
  'ig.wh.attempts': { one: "{count} attempt", other: "{count} attempts" },
  'ig.wh.inspect': "Inspect",
  'ig.wh.replay': "Replay",
  'ig.wh.replayReason': "Replayed from the delivery log",
  'ig.cl.title': "API clients",
  'ig.cl.intro': "Organisation-scoped clients. Scopes are grants; actions reserved for people are never available to clients.",
  'ig.cl.openapi': "API description (OpenAPI)",
  'ig.cl.empty': "No API clients.",
  'ig.cl.client': "Client",
  'ig.cl.scopes': "Scopes",
  'ig.cl.scopesHint': "Comma-separated actions, for example site.read, visit.read.site.",
  'ig.cl.tokens': "Tokens",
  'ig.cl.rate': "{count} requests per minute",
  'ig.cl.expires': "expires {at}",
  'ig.cl.lastUsed': "last used {at}",
  'ig.cl.rotate': "Rotate token",
  'ig.cl.disable': "Disable client",
  'ig.cl.disableReason': "Disabled from the API clients view",
  'ig.cl.new': "New API client",
  'ig.cl.newHint': "The token is shown once and expires; rotate it before expiry.",
  'ig.cl.name': "Client name",
  'ig.cl.create': "Create client",
  'ig.cl.token': "API token for {name}",
  'ig.tab.connectors': "Connectors",
  'ig.tab.devices': "Devices",
  'ig.tab.webhooks': "Webhooks",
  'ig.tab.clients': "API clients",
  'ig.transport.connected': "Connected",
  'ig.transport.degraded': "Degraded",
  'ig.transport.disconnected': "Disconnected",
  'ig.transport.never_connected': "Never connected",
  'ig.business.confirmed': "Confirmed",
  'ig.business.attention': "Needs attention",
  'ig.business.unknown': "Unknown result",
  'ig.business.none': "No operations yet",
  'ig.outcome.confirmed': "Confirmed",
  'ig.outcome.failed': "Failed",
  'ig.outcome.unknown': "Unknown",
  'ig.outcome.unsupported': "Unsupported",
  'ig.op.test_connection': "Test connection",
  'ig.op.read_state': "Read state",
  'ig.op.sync_people': "Synchronise people",
  'ig.op.issue': "Issue",
  'ig.op.revoke': "Revoke",
  'ig.op.observe': "Observe",
  'ig.op.send': "Send",
  'ig.op.print': "Print",
  'ig.ad.v.simulator': "Simulator (development only)",
  'ig.ad.v.unvalidated': "Not validated",
  'ig.con.status.draft': "Draft",
  'ig.con.status.enabled': "Enabled",
  'ig.con.status.disabled': "Disabled",
  'ig.dev.kind.printer': "Badge printer",
  'ig.dev.kind.scanner': "Scanner",
  'ig.dev.kind.reader': "Document reader",
  'ig.dev.kind.encoder': "Card encoder",
  'ig.dev.kind.edge_agent': "Edge agent",
  'ig.dev.kind.kiosk': "Kiosk",
  'ig.job.completed': "Completed",
  'ig.job.failed': "Failed",
  'ig.job.unknown': "Unknown",
  'ig.wh.status.pending_verification': "Awaiting verification",
  'ig.wh.status.active': "Active",
  'ig.wh.status.paused': "Paused",
  'ig.wh.status.disabled': "Disabled",
  'ig.wh.dstatus.pending': "Pending",
  'ig.wh.dstatus.delivered': "Delivered",
  'ig.wh.dstatus.dead': "Dead letter",
  'ig.wh.dstatus.cancelled': "Cancelled",
  'ig.wh.outcome.delivered': "Delivered",
  'ig.wh.outcome.rejected': "Rejected",
  'ig.wh.outcome.timeout': "Timed out (unknown)",
  'ig.wh.outcome.network_error': "Network error",
  'ig.wh.outcome.blocked_destination': "Destination refused",
  'ig.cl.status.active': "Active",
  'ig.cl.status.disabled': "Disabled",
  'ig.cl.token.active': "Active",
  'ig.cl.token.expired': "Expired",
  'ig.cl.token.revoked': "Revoked",
  'ig.event.visit.created': "A visit was planned (draft or submitted).",
  'ig.event.visit.submitted': "A visit was submitted for approval.",
  'ig.event.visit.approved': "All approval stages for one participant are approved. Approval is not presence or access.",
  'ig.event.visit.rejected': "An approval stage rejected one participant.",
  'ig.event.visit.cancelled': "A visit was cancelled. Cancellation does not assert that anyone left.",
  'ig.event.visitor.checked_in': "An arrival was observed (presence fact; not approval or credential state).",
  'ig.event.visitor.checked_out': "A departure was observed. Credential revocation is a separate, possibly pending, fact.",
  'ig.event.credential.revocation_requested': "Logical credential revocation was requested. The provider outcome is reported separately.",
  'ig.event.delivery.handed_over': "A delivery handover or refusal was recorded at the desk.",
  'ig.event.vehicle.entered': "A vehicle entry was observed and recorded.",
  'ig.event.vehicle.exited': "A vehicle exit was observed and recorded.",
  'ig.event.vehicle.expected': "An expected vehicle was attached to a visit (expectation, not permission).",
} as const;
